import type { NexperienceVariantFragment } from '@data-access/lib/gql/cart/documents'

import type { NexperienceDayPricesQuery } from '@data-access/lib/gql/experience/documents'

export type ExperienceDayPrice = {
  basePrice: number | null
  priceCategory: string | null
  priceByPriceId: Map<string, number>
}
export type ExperienceDayPrices = Map<string, ExperienceDayPrice>

export const toDayPrices = (data: NexperienceDayPricesQuery): ExperienceDayPrices =>
  new Map(
    data.nexperienceDayPrices.map(day => [
      day.date,
      {
        basePrice: day.basePrice ?? null,
        priceCategory: day.priceCategory ?? null,
        priceByPriceId: new Map(day.prices.map(({ priceId, price }) => [priceId, price])),
      },
    ])
  )

type RepriceableVariant = {
  options: { priceId?: string | null; price: number }[]
}

export function repriceVariants<T extends RepriceableVariant>(
  variants: T[],
  dayPrice?: ExperienceDayPrice
): T[] {
  if (!dayPrice || dayPrice.priceByPriceId.size === 0) return variants

  let changed = false
  const repriced = variants.map(variant => ({
    ...variant,
    options: variant.options.map(option => {
      const price = option.priceId ? dayPrice.priceByPriceId.get(option.priceId) : undefined
      if (price === undefined || price === option.price) return option
      changed = true
      return { ...option, price }
    }),
  }))

  return changed ? repriced : variants
}

export function cheapestPrice(variants: NexperienceVariantFragment[]): number | undefined {
  const prices = variants.flatMap(variant => variant.options.map(option => option.price))
  return prices.length === 0 ? undefined : Math.min(...prices)
}

export function cheapestDayPrice(dayPrice?: ExperienceDayPrice): number | undefined {
  if (!dayPrice || dayPrice.priceByPriceId.size === 0) return undefined
  const prices = [...dayPrice.priceByPriceId.values()]
  const paidPrices = prices.filter(price => price > 0)
  return Math.min(...(paidPrices.length > 0 ? paidPrices : prices))
}

export type HeadlinePrice = { price: number; category: string } | null

export function headlinePrice(
  fallback: HeadlinePrice,
  dayPrice?: ExperienceDayPrice
): HeadlinePrice {
  if (!dayPrice || dayPrice.basePrice === null) return fallback

  const category = dayPrice.priceCategory ?? fallback?.category
  if (!category) return fallback

  return { price: dayPrice.basePrice, category }
}
